<template>
  <div>
    <h2 v-if="currentUser" class="mb-1 mt-0">
      Welcome {{ currentUser.username }}, here's your stats:
    </h2>
    <div class="row mt-sm-3 mb-4 text-center">
      <span class="col-12 col-sm-4 mb-2 mb-sm-0 p-0">
        <svg-icon
          icon="distance"
          fill-color="#174776"
          width="50px"
          height="50px"
          stroke-color="none"
        />
        <p class="color-black">Distance travelled:</p>
        <strong>{{ parseFloat(totalMi).toFixed(2) }} miles</strong>
      </span>
      <span class="col-12 col-sm-4 mb-2 mb-sm-0 p-0 border-left border-right">
        <svg-icon
          icon="mountains"
          fill-color="#174776"
          width="50px"
          height="50px"
          stroke-color="none"
        />
        <p class="color-black">Height climbed:</p>
        <strong>{{ numberWithCommas(parseFloat(totalEl).toFixed(0)) }} feet</strong>
      </span>
      <span class="col-12 col-sm-4 mb-2 mb-sm-0 p-0">
        <svg-icon
          icon="time"
          fill-color="#174776"
          width="50px"
          height="50px"
          stroke-color="none"
        />
        <p class="color-black">Time spent:</p>
        <strong>{{ totalTi }}</strong>
      </span>
    </div>

    <span v-if="showDistProg" class="row mt-sm-3">
      <span class="pt-1 pb-1 pl-0 col-12 col-sm-8 text-sm-left">Lands End to John O'Groats <small class="text-muted">{{ quotientMi + getOrdinal(quotientMi) }} lap</small></span>
      <h3 class="p-1 text-right text-muted col-12 col-sm-4 mb-0 mt-0"><strong>{{ parseFloat(remainderMi).toFixed(2) }}</strong> of <strong>980</strong> miles</h3>
    </span>
    <div v-if="showDistProg" class="bar col-12 pl-0 pr-0 w-100">
      <div class="bar-width d-block" :style="{ width: progress + '%' }"></div>
      <span class="bar-target">
        Road to {{ currentStage }}
      </span>
      <span class="bar-digits text-right">
        {{ parseFloat(progress).toFixed(2) }}%
      </span>
    </div>

    <span v-if="showElvProg" class="row mt-3">
      <span class="pt-1 pb-1 pl-0 col-12 col-sm-8 text-sm-left">Climbing Everest <small class="text-muted">{{ quotientEl + getOrdinal(quotientEl) }} ascent</small></span>
      <h3 class="p-1 text-right text-muted col-12 col-sm-4 mb-0 mt-0"><strong>{{ numberWithCommas(parseFloat(remainderEl).toFixed(0)) }}</strong> of <strong>29,030</strong> feet</h3>
    </span>
    <div v-if="showElvProg" class="bar col-12 pl-0 pr-0 w-100">
      <div class="bar-width d-block" :style="{ width: progressUpEverest + '%' }"></div>
      <span class="bar-target">
        {{ currentCamp }}
      </span>
      <span class="bar-digits text-right">
        {{ parseFloat(progressUpEverest).toFixed(2) }}%
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import SvgIcon from './svg-icon.vue';
import { numberWithCommas, secondsToHms, getOrdinal } from '../composables/useFormatters.js';
import { applyOverrideDistances } from '../composables/useAthleteData.js';

const MI = 1609.344;
const FT = 3.28084;

const camps = [
  { name: 'Travelling to Katmandu', alt: 4600 },
  { name: 'Treking to Base Camp', alt: 17700 },
  { name: 'Climbing to Camp I', alt: 19900 },
  { name: 'Hiking to Camp II', alt: 21300 },
  { name: 'Struggling to Camp III', alt: 24500 },
  { name: 'Camp IV is in sight', alt: 26000 },
  { name: 'Time to summit!', alt: 29030 },
];

const stages = [
  { name: 'Okehampton', alt: 105 },
  { name: 'Bath', alt: 215 },
  { name: 'Ludlow', alt: 316 },
  { name: 'Haydock', alt: 423 },
  { name: 'Carlisle', alt: 553 },
  { name: 'Edinburgh', alt: 659 },
  { name: 'Strathdon', alt: 771 },
  { name: 'Kyle of Sutherland', alt: 890 },
  { name: "John O'Groats!", alt: 980 },
];

const props = defineProps({
  activities: {
    type: Array,
    required: true,
  },
  week: {
    type: Array,
    required: false,
    default: () => [],
  },
  user: {
    type: Object,
    required: false,
    default: null,
  },
});

const currentUser = ref(props.user);
const progress = ref(0);
const progressUpEverest = ref(0);
const quotientMi = ref(0);
const remainderMi = ref(0);
const quotientEl = ref(0);
const remainderEl = ref(0);
const currentCamp = ref('Travelling to Kathmandu');
const currentStage = ref('Okehampton');
const showDistProg = ref(true);
const showElvProg = ref(true);
const totalMi = ref(0);
const totalEl = ref(0);
const totalTi = ref('');

onMounted(() => {
  // Apply override distances
  const activities = applyOverrideDistances([...props.activities]);
  applyOverrideDistances([...props.week]);

  let totalM = 0;
  let totalE = 0;
  let totalT = 0;

  for (const activity of activities) {
    totalM += parseFloat(activity.distance);
    totalT += parseFloat(activity.moving_time);
    if (activity.elevation_gain !== '') {
      totalE += parseFloat(activity.elevation_gain);
    }
  }

  totalMi.value = totalM / MI;
  totalEl.value = totalE * FT;
  totalTi.value = secondsToHms(totalT);

  quotientMi.value = Math.floor(totalMi.value / 980) + 1;
  remainderMi.value = totalMi.value % 980;
  progress.value = (remainderMi.value / 980) * 100;

  quotientEl.value = Math.floor(totalEl.value / 29030) + 1;
  remainderEl.value = totalEl.value % 29030;
  progressUpEverest.value = (remainderEl.value / 29030) * 100;

  for (let i = 0; i < camps.length; i++) {
    if (remainderEl.value > camps[i].alt && camps[i + 1]) {
      currentCamp.value = camps[i + 1].name;
    }
  }

  for (let i = 0; i < stages.length; i++) {
    if (remainderMi.value > stages[i].alt && stages[i + 1]) {
      currentStage.value = stages[i + 1].name;
    }
  }
});
</script>

<style lang="scss" scoped>
h2.active {
  font-weight: 900;
  color: #174776
}

.bar {
  position: relative;
  background: white;
  border-radius: 25px;
  overflow: hidden;
  .bar-name {
    position: absolute;
    left: 50%;
    top: 12px;
    text-transform: capitalize;
    color: #243546;
    font-weight: 700;
  }
  .bar-width {
    background-color: #174776;
    height: 32px;
    border-radius: 25px;
  }
}

.bar-target {
  position: absolute;
  left: 3px;
  top: 3px;
  color: #174776;
  display: inline-block;
  background: rgba(255, 255, 255, 0.8);
  padding: 6px;
  border-radius: 16px;
  font-size: 14px;

  &.on-target {
    color: #02a517
  }
}

.bar-digits {
  position: absolute;
  right: 15px;
  top: 7px;
  text-transform: capitalize;
  color: #174776;
  display: inline-block;
  background: rgba(255, 255, 255, 0.8);
  padding: 6px;
  border-radius: 16px;
  font-size: 14px;
}

.stats-btn {
  cursor: pointer;
}
</style>
